import { useState, type HTMLAttributes } from 'react';
import { cx } from '../internal/cx';
import { avatarColorIndex, getInitials } from './avatarUtils';
import styles from './Avatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  /** Person or entity name; used for initials, color and the accessible name. */
  name: string;
  src?: string | null;
  size?: AvatarSize;
  /** Accessible text. Defaults to `name`. */
  alt?: string;
  /** Hide from assistive technology when the name is already shown next to the avatar. */
  decorative?: boolean;
}

export function Avatar({
  name,
  src,
  size = 'md',
  alt,
  decorative = false,
  className,
  style,
  ...rest
}: AvatarProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = Boolean(src) && failedSrc !== src;
  const label = alt ?? name;
  const colorIndex = avatarColorIndex(name.trim().toLowerCase());

  return (
    <span
      className={cx(styles.avatar, styles[size], className)}
      style={{ backgroundColor: `var(--color-avatar-${colorIndex})`, ...style }}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
      {...rest}
    >
      {showImage && src ? (
        <img
          className={styles.image}
          src={src}
          alt=""
          onError={() => setFailedSrc(src)}
          draggable={false}
        />
      ) : (
        <span className={styles.initials} aria-hidden="true">
          {getInitials(name)}
        </span>
      )}
    </span>
  );
}

export interface AvatarGroupProps extends HTMLAttributes<HTMLSpanElement> {
  people: { id?: string; name: string; src?: string | null }[];
  max?: number;
  size?: AvatarSize;
}

export function AvatarGroup({
  people,
  max = 4,
  size = 'sm',
  className,
  ...rest
}: AvatarGroupProps) {
  const visible = people.slice(0, max);
  const overflow = people.length - visible.length;
  return (
    <span className={cx(styles.group, className)} {...rest}>
      {visible.map((person, index) => (
        <Avatar
          key={person.id ?? `${person.name}-${index}`}
          name={person.name}
          src={person.src ?? null}
          size={size}
          className={styles.groupItem}
        />
      ))}
      {overflow > 0 && (
        <span
          className={cx(styles.avatar, styles[size], styles.groupItem, styles.overflow)}
          role="img"
          aria-label={`and ${overflow} more`}
        >
          <span aria-hidden="true">+{overflow}</span>
        </span>
      )}
    </span>
  );
}
